//@ts-check

/**
 * Return a debounced function that delays invoking the provided function
 * @param {Function} fn
 * @param {number} [ms=1000] milliseconds to wait before invoking function
 * @returns {Function} debounced function
 */

function debounce(fn, ms) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      fn.apply(this, args);
    }, ms);
  };
}

/**
 * Wrap function in a debounced async function
 * Rejects previous pending calls with DebounceError
 * @param {Function} fn function to debounce
 * @param {number} [ms=1000] milliseconds to wait before invoking function
 * @param {string?} fnName optional name of the function
 * @returns {Function} debounced async function
 */
function debounceAsync(fn, ms = 1000, fnName = "") {
  let timeout;
  let pendingReject = null;
  let pendingResult = null;

  return function (...args) {
    if (pendingReject) {
      pendingReject(new DebounceError(fnName ?? fn.name));
    }
    clearTimeout(timeout);
    return new Promise((resolve, reject) => {
      pendingReject = reject;
      timeout = setTimeout(() => {
        try {
          pendingReject = null;
          pendingResult = fn(...args);
        } catch (error) {
          reject(error);
          return;
        }
        if (pendingResult instanceof Promise) {
          pendingResult.then(resolve).catch(reject);
        } else {
          resolve(pendingResult);
        }
      }, ms);
    });
  };
}

/**
 * Custom error for debounced functions
 * Useful for filtering debounce rejections
 * @extends Error
 */
class DebounceError extends Error {
  constructor(functionName) {
    super();
    this.message = "Debounced " + functionName;
  }
}

export { debounce, debounceAsync, DebounceError };
